import React, {Fragment, isValidElement, useEffect, useRef, useState} from 'react'
import {tailCheck, useObserver, useRerender} from "./CommonReact";
import {Button, Checkbox} from "./Inputs";
import {ListItemValue, Title} from "../app/CommonUIComponenets";
import {Badge, TextEllip, TitleWithBadge} from "./UIBlocks";
import {commafyPlural} from "./CommonJS";
import {AnimatePickerRotation} from "./UIIcons";
import {ToggleDiv} from "./UIAnimateToggleDiv";
import {genTooltip} from "../app/AppContext";
import {severitiesDefsArr} from "../app/CommonUIData";


export function Switch ({value, field, opts, onChange, className, tip}) {
    const curIdx = opts.findIndex(o => value === o.k);

    return <div {...genTooltip(tip, "middle")}
            className={`flex relative transition-all duration-75 ease-in-out bg-bg4 cursor-pointer p-1 h-6 w-12 rounded-xl 
             shadow-[inset_2px_2px_8px_rgba(0,_0,_0,_0.4)] ${className}`}
            onClick={() => onChange(field, !value)}>
            <div className={`absolute min-w-0 ${curIdx ? "left-1.5" : "left-[55%]"}
             duration-100 bg-prm-light ease-in-out rounded-full transition-all h-4 aspect-square min-w-0 shrink-0 
             shadow-[-2px_2px_8px_rgba(0,_0,_0,_0.4)]`}/>
            <div className={`absolute ${!curIdx ? "left-1" : "left-[55%]"} transition-all text-title fill-title`}>
                {curIdx ? opts[0].i : opts[1].i}</div>
    </div>;
};

export function UIRadioGroup({value, field, opts, onChange, className, classNameBtn, classNameN, classNameMark, classNameText, disabled,
                                 toggle, ...props}) {
    const rBar = useRef();
    const rerender = useRerender();
    const curIdx = opts?.findIndex(o => value === o.k);
    const curElem = rBar.current?.children[curIdx+1];
    useObserver(ResizeObserver, rBar, "width", rerender, {debounceMs: 100});   // allow animated box to redraw after initial layout or scrollbar appearance

    return (
        <div className={tailCheck(className, "select-none flex relative", "w-max",
            disabled && "opacity-60", !toggle? "cursor-default" : "cursor-pointer")}
             onClick={disabled || !toggle? undefined : e => onChange(field, opts[(curIdx + 1) % opts.length].k)}
             {...props} ref={rBar} draggable="false"
        >
            <div className={tailCheck(classNameMark, "absolute transition-all duration-300 ease-out", "bg-gray-800/80")}
                 style={{width: curElem?.offsetWidth + "px", height: (curElem?.offsetHeight) + "px", transform: `translate(${curElem?.offsetLeft}px,${curElem?.offsetTop}px)`}}/>
            {opts?.map((opt) => (
                <div data-test={`ui-radio-group-${`${opt.k}`.replace("- ","").toLowerCase().toKebabCase()}`} key={opt.k} {...genTooltip(opt.tip)}
                     onClick={e => opt.k !== value && !disabled && !opt.disabled && onChange(field, opt.k)}
                     className={tailCheck(classNameBtn,
                         "flex items-center gap-2 transition ease-out duration-300 z-[1] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75",
                         "justify-center",
                         opt.k === value? "X bg-transparent" :
                             !disabled && !opt.disabled? "cursor-pointer opacity-60 hover:opacity-100" :
                                 "cursor-not-allowed opacity-40",
                         !classNameBtn?.match(/(^| )p\w?-/) && "px-2 py-1",
                     )}>
                    <TextEllip className={`${opt.k === value && "text-prm-contrast"} ${classNameText}`}>{opt.h}</TextEllip>
                    {opt.n !== undefined && <Badge pill className={`${classNameN} shrink-0 bg-subtitle1/40 ${opt.k !== value? "text-title" : "text-prm-contrast"}`}>{opt.n}</Badge>}
                </div>
                ))}
        </div>
    );
}

export const FilterRadioGroup = ({className, wrap = false, disabled = false, ...rest}) =>
    <UIRadioGroup
        className={tailCheck(className, "bg-bg2", "rounded-md", "overflow-hidden")} disabled={disabled}
        classNameMark="bg-prm-main" classNameBtn={`text-2xs border-r-subtitle2 border-r last:border-r-transparent ${
        wrap ? "flex-1 text-center px-3 py-2" : "whitespace-nowrap px-4 py-3"}`}
        {...rest}/>;

export const FilterList = ({w, ...rest}) =>
    <UIRadioGroup
        className="bg-transparent gap-2 flex-col flex-wrap w-full"
        classNameBtn={`bg-bubble text-xs rounded-md px-4 py-2 w-full justify-between ${w}`}
        classNameMark="bg-prm-main rounded-md"
        {...rest}/>;


export function UIMultiSelect({selKeys, field, opts, onChange, className, classNameBtn, classNameMark, disabled, allIfNone, ...props}) {
    selKeys = selKeys ?? [];
    return (
        <div className={tailCheck(className, "flex flex-wrap", "w-fit", "relative select-none", disabled && "opacity-60")}
             draggable="false" {...props}>
            {opts.map((opt) => {
                const isSel = selKeys.includes(opt.k);
                const isAll = allIfNone && !selKeys.length;
                return (
                    <div key={opt.k}
                         onClick={e => !disabled && onChange(field, isSel? selKeys.filter(k => k !== opt.k) : [...selKeys, opt.k])}
                         className={tailCheck(classNameBtn,
                             "inline-flex items-center leading-none transition ease-out duration-300 z-[1]",
                             "focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75",
                             "px-2 py-1", "justify-center",
                             (!isSel && !isAll) && !disabled && "opacity-50 hover:opacity-75",
                             (isSel || isAll) && (classNameMark || "text-white bg-prm-main"),
                             !disabled? "cursor-pointer" : "cursor-not-allowed")}
                    >
                        {opt.h}
                    </div>
                );
            })}
        </div>
    );
}


export function SeverityPicker({field, selKeys, onChange, severityVals, className, classNameBtn}) {
    return <MultiSelectListItems
        {...{field, selKeys, onChange}}
        opts={severityVals.map(({k, v}) => ({k, v, h: severitiesDefsArr[k].h, border: severitiesDefsArr[k].border}))}
        className={`flex ${severityVals?.length === 4 && "w-"} ${className}`}
        classNameBtn={`min-w-14 m- ${severityVals?.length < 4 && "grow-"} ${classNameBtn}`}
    />;
}

export function MultiSelectListItems({field, selKeys, onChange, opts, className, classNameBtn, classNameItem}) {
    opts = opts.map(({k, h, v, c, border}) => ({k, h:
            <ListItemValue
                h={v !== undefined? h : undefined} val={v ?? h}
                className={tailCheck(classNameItem, "w-full", "pb-1 overflow-visible", "text-2xs", "border-b-[3px]", border)}/>
    }));

    return <UIMultiSelect
        {...{field, selKeys, onChange, opts}} allIfNone
        className={tailCheck(className, "shrink-0 rounded-md bg-bg1/40", !className?.startsWith("flex") && "grid grid-cols-2 w-")}
        classNameBtn={tailCheck(classNameBtn, "text-xs text-center rounded border-r-bg2 not-last:border-r py-1", "grow", "m-1")}
        classNameMark="bg-bg1"
    />;
}

export function UICheckBoxGroup({field, opts, onChange, className, checkboxClassName, value, row = false }) {
    const changeChecked = (checkedBox) => {
        const newVal = value.includes(checkedBox) ? value.filter(k=> k !== checkedBox) : [checkedBox, ...value];
        onChange(field, newVal);
    };

    return <div className={`flex ${className} ${row ? "gap-4" : "gap-1 flex-col"}`}>
        {opts.map(opt =>
            <Checkbox
                key={opt.k}
                className={`${row ? "gap-1" : "gap-3"} ${checkboxClassName}`}
                field={opt.k}
                isChecked={value.includes(opt.k)}
                label={
                    <div className="flex items-center gap-1">
                        {opt.c && <div className={`w-2 h-2 mr-1 ${opt.c}`} style={{background: opt.c}}/>}
                        <Title>{opt.h}</Title>
                    </div>}
                onChange={changeChecked}
            />)}
    </div>
}

/**
 * Displays an animated tabs row.
 * @param tabs array of tabs: [string | function | "sep" | {h?: string, d?(): renderer, unavail?: bool, className?: string}]
 * @param onClick function(index): called when user clicks on a tab.
 * @param iTab number: index of currently selected tab.
 * @param className string?: top level classes
 * @param classNameTab string?:  common classes for tab elements
 * @param classNameUnderline string?: classes for dynamic underline. default: "top-8"
 * @param renderer function(tab, i)?:
 */
export function UITabsBar({tabs, onClick, iTab, className, classNameTab, classNameUnderline, renderer}) {
    const rBar = useRef();
    const rerender = useRerender();
    const curElem = rBar.current?.children[iTab+1];
    useEffect(() => rerender(), [tabs]);            // allow animated box to redraw after initial layout

    return (
        <div data-test="ui-tab-bar" className={`relative flex gap-2 whitespace-nowrap ${className}`} ref={rBar}>
            <div style={{width: curElem?.offsetWidth + "px", height: "3px", transform: `translateX(${curElem?.offsetLeft}px)`}}
                 className={`transition-all duration-300 ease-out absolute bg-prm-main ${classNameUnderline || "top-8"}`}/>
            {tabs.map((tab, i) => tab === "sep"?
                <div key={i} className="mx-4 h-3 mt-3 bg-gray-400 w-[2px] focus:outline-none"/>
                : <Button
                    data-test={`${i}-tab`}
                    key={i} {...genTooltip(tab.tip)}
                    onClick={iTab === i || tab.unavail? undefined : (e => onClick(i))}
                    className={`px-3 text-center z-10 border-transparent ${
                        !classNameTab?.match(/(^| )text-/) && "text-title"} ${
                        iTab !== i? "[transition-property:all] opacity-50 hover:opacity-70" : "opacity-100"} ${
                        tab.unavail && "cursor-not-allowed"} ${
                        tab.className} ${
                        classNameTab}`}
                >
                    {renderer? renderer(tab, i) :
                        typeof tab === "string" || isValidElement(tab)? tab :
                            typeof tab === "function"? tab(i, rerender) :
                                (tab.d?.(tab, rerender) ?? tab.h)}
                </Button>
            )}
        </div>
    );
}

export const TabsWithNum = (tabs, tipFormat) => tabs.map(({h, v}) => <TitleWithBadge val={v} {...{h, tipFormat}}/>);


export function MenuTree({menuArr, className, notifsPaths, classNameText, field, value, onChange, openAbove = false, depth = 0, classNameSub,
                             classNameHead, classNameSelSub, onSelect, canSelectHead = false, subMenuOpenPred = (subOpen, path) => subOpen === path}) {
    const selPath = findSubPath({menu: menuArr, value});
    const [subOpen, setSubOpen] = useState(selPath);

    const menuItem = ({h, icon, perms, subMenu, staticMenu, k, params, tip, openAbove, n}, path, depth = 0) => {
        const isSelected = path === selPath;
        const openSubMenu = () => setSubOpen(prev => prev === path ? "" : path);
        const currNotifs = notifsPaths?.filter(({path: p}) => p.startsWith(path)), notifsCount = currNotifs?.reduce((acc, val) => acc + val.count, 0);

        const onClick = () => {
            subMenu && openSubMenu();
            if (k && (!subMenu || canSelectHead)) {
                onChange?.(field, k);
                onSelect?.(k, params);
            }
        }

        return <Fragment key={path}>
            <Button
                data-test={`menu-item`}
                className={`${tailCheck((subMenu || !depth) ? classNameHead : classNameSub,
                    "gap-2", "rounded-lg", "h-10", "py-2", depth ? "px-2" : "mt-6")} ${
                    isSelected && ((!subMenu && depth) && classNameSelSub ? classNameSelSub : "active:bg-blue-500 bg-prm-main text-prm-contrast")}`}
                style={{marginLeft: (!depth ? 0 : depth - 1) + "rem"}} disabled={!k && !subMenu}
                onClick={staticMenu ? undefined : onClick}
                {...genTooltip(tip)}
            >
                {!depth ? null : typeof icon === "string" ? <i className={`icon-${icon} w-4 text-base`}/> :
                    <div className={isSelected ? "fill-prm-contrast" : "fill-subtitle1"}>{icon}</div>}
                <Title className={tailCheck(classNameText, "grow", "font-light", depth ? "text-2sm" : "text-lg")}
                       classNameColor={"text-inherit"}>{h}</Title>
                {!!currNotifs?.length && (!subMenu || !subMenuOpenPred(subOpen, path)) &&
                    <div {...genTooltip(`${commafyPlural(notifsCount, "record")} of ${currNotifs.map(v => v.desc).join(", ")} are pending.`)}
                         className="shrink-0 px-1 text-2xs text-center animate-appear bg-sec/60 rounded-full text-prm-contrast">{notifsCount}</div>}
                {n && <Badge pill className={`shrink-0 opacity-60 hover:opacity-100 bg-subtitle1/40 text-prm-contrast text-xs ${isSelected ? "text-title" : "text-prm-contrast"}`}>{n}</Badge>}
                {subMenu && !staticMenu && <AnimatePickerRotation className={value === k ? "rotate-180" : "font-bolder"}/>}
            </Button>
            {subMenu &&
                <ToggleDiv show={staticMenu || subMenuOpenPred(subOpen, path)} className={openAbove && "flex flex-col-reverse"}>
                    {subMenu.map((item, idx) => menuItem(item, `${path}:${idx}`, depth + 1))}
                </ToggleDiv>}
        </Fragment>;
    };

    return (
        <div className={`flex ${openAbove ? "flex-col-reverse" : "flex-col"} gap-0 ${className}`}>
            {menuArr.map((item, idx) => menuItem(item, idx + "", depth))}
        </div>
    );
}

export function findNotifPath(menu, type, count, path) {
    return menu.findPred(({subMenu, notifs, pt}, i) =>
        (pt = (path ? path + ":" : "") + i) &&
        Object.keys(notifs || []).includes(type) ?
            ({path: pt, desc: notifs[type], count}) :
            subMenu && findNotifPath(subMenu, type, count, pt)
    );
}

function findSubPath({menu, path, value}) {
    return menu.findPred(({k, subMenu, pt}, i) =>
        (pt = (path ? path + ":" : "") + i) && value === k ? pt : subMenu && findSubPath({menu: subMenu, value, path: pt}));
}